import React from 'react'
import { motion } from 'framer-motion'
import { 
  Play, Calendar, MapPin, Star, ArrowRight, Users, ShoppingBag, 
  MessageSquare, Music, Headphones, Mic, Volume2, TrendingUp, Clock 
} from 'lucide-react'

const HeroSection = () => {
  const [currentEventIndex, setCurrentEventIndex] = React.useState(0)

  const tonightEvents = [
    {
      id: 1,
      title: 'Jazz Fusion Night',
      artist: 'Wichita Jazz Collective',
      venue: 'The Wave',
      time: '8:00 PM',
      price: '$15',
      status: 'live',
      image: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&cs=tinysrgb&w=400',
      attendees: 89,
      rating: 4.9
    },
    {
      id: 2,
      title: 'Open Mic Wednesday',
      artist: 'Various Artists',
      venue: 'Kirby\'s Beer Store',
      time: '7:00 PM',
      price: 'Free',
      status: 'upcoming',
      image: 'https://images.pexels.com/photos/1190297/pexels-photo-1190297.jpeg?auto=compress&cs=tinysrgb&w=400',
      attendees: 23,
      rating: 4.7
    },
    {
      id: 3,
      title: 'Thunder Road Live',
      artist: 'Thunder Road',
      venue: 'Lava Tonic',
      time: '9:30 PM',
      price: '$25',
      status: 'upcoming',
      image: 'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&w=400',
      attendees: 156,
      rating: 4.8
    },
  ]

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentEventIndex((prev) => (prev + 1) % tonightEvents.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [tonightEvents.length])

  const quickStats = [
    { label: 'Events Tonight', value: '12', icon: Calendar, color: 'from-le-accent to-purple-600' },
    { label: 'Active Artists', value: '500+', icon: Users, color: 'from-le-accent-2 to-blue-600' },
    { label: 'Gear Listed', value: '200+', icon: ShoppingBag, color: 'from-le-amber to-orange-600' },
    { label: 'Forum Topics', value: '50+', icon: MessageSquare, color: 'from-green-500 to-emerald-600' },
  ]

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 hero-gradient"></div>
        <div className="absolute top-20 left-20 w-80 h-80 bg-le-accent/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-le-accent-2/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '4s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-le-amber/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="container-max section-padding relative z-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Main Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {/* Live Status Badge */}
            <motion.div
              className="inline-flex items-center space-x-3 glass-card px-6 py-3 rounded-full mb-8 animate-pulse-glow"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-white">Live Events Happening Now</span>
              <TrendingUp size={16} className="text-le-accent-2" />
            </motion.div>

            {/* Main Headline */}
            <motion.h1 
              className="text-6xl lg:text-8xl font-display font-bold text-white mb-8 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              Wichita's
              <span className="text-gradient block relative">
                Music Hub
                <motion.div
                  className="absolute -inset-4 bg-gradient-to-r from-le-accent/20 to-le-accent-2/20 rounded-2xl blur-xl"
                  animate={{ 
                    scale: [1, 1.1, 1],
                    opacity: [0.3, 0.6, 0.3]
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                />
              </span>
            </motion.h1>

            <motion.p 
              className="text-2xl text-white/80 mb-12 leading-relaxed max-w-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              Discover tonight's hottest shows, connect with local artists, trade gear, 
              and celebrate real performance in Kansas's most vibrant music community.
            </motion.p>

            {/* Action Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-6 mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              <button className="btn-primary text-xl px-12 py-6 flex items-center justify-center space-x-3">
                <Calendar size={24} />
                <span>Explore Tonight's Shows</span>
              </button>
              <button className="btn-secondary text-xl px-12 py-6 flex items-center justify-center space-x-3">
                <Play size={24} />
                <span>Watch Community Video</span>
              </button>
            </motion.div>

            {/* Quick Stats Grid */}
            <motion.div
              className="grid grid-cols-2 lg:grid-cols-4 gap-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
            >
              {quickStats.map((stat, index) => {
                const Icon = stat.icon
                return (
                  <motion.div
                    key={stat.label}
                    className="glass-card p-6 text-center card-hover group"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                  >
                    <div className={`w-16 h-16 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform duration-300`}>
                      <Icon size={28} className="text-white" />
                    </div>
                    <div className="text-3xl font-display font-bold text-white mb-2">{stat.value}</div>
                    <div className="text-sm text-white/70 font-medium">{stat.label}</div>
                  </motion.div>
                )
              })}
            </motion.div>
          </motion.div>

          {/* Right: Live Event Carousel */}
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            {/* Header */}
            <div className="text-center mb-8">
              <h3 className="text-3xl font-display font-bold text-white mb-3">
                Happening Tonight
              </h3>
              <p className="text-white/70 text-lg">Live updates from Wichita venues</p>
              
              {/* Carousel Indicators */}
              <div className="flex justify-center space-x-3 mt-6">
                {tonightEvents.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentEventIndex(index)}
                    className={`transition-all duration-300 rounded-full ${
                      index === currentEventIndex 
                        ? 'w-8 h-3 bg-gradient-to-r from-le-accent to-le-accent-2' 
                        : 'w-3 h-3 bg-white/30 hover:bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Featured Event Card */}
            <motion.div
              key={currentEventIndex}
              className="glass-card overflow-hidden card-hover group relative"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              whileHover={{ scale: 1.02 }}
            >
              {tonightEvents[currentEventIndex].status === 'live' && (
                <div className="absolute top-4 right-4 z-10 flex items-center space-x-2 bg-red-500 text-white text-sm px-4 py-2 rounded-full font-bold status-live">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  <span>LIVE NOW</span>
                </div>
              )}

              <div className="relative h-64 overflow-hidden">
                <img
                  src={tonightEvents[currentEventIndex].image}
                  alt={tonightEvents[currentEventIndex].title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent"></div>
                
                {/* Floating Stats */}
                <div className="absolute top-4 left-4 flex space-x-3">
                  <div className="glass-card px-3 py-2 flex items-center space-x-2">
                    <Star size={14} className="text-yellow-400 fill-current" />
                    <span className="text-white font-medium">{tonightEvents[currentEventIndex].rating}</span>
                  </div>
                  <div className="glass-card px-3 py-2 flex items-center space-x-2">
                    <Users size={14} className="text-le-accent-2" />
                    <span className="text-white font-medium">{tonightEvents[currentEventIndex].attendees}</span>
                  </div>
                </div>
              </div>

              <div className="p-8">
                <h4 className="text-2xl font-display font-bold text-white mb-3 group-hover:text-le-accent transition-colors duration-300">
                  {tonightEvents[currentEventIndex].title}
                </h4>
                <p className="text-lg text-white/80 mb-4">{tonightEvents[currentEventIndex].artist}</p>
                
                <div className="grid grid-cols-2 gap-4 text-white/70 mb-6">
                  <div className="flex items-center space-x-2">
                    <MapPin size={18} className="text-le-accent-2" />
                    <span className="font-medium">{tonightEvents[currentEventIndex].venue}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock size={18} className="text-le-accent" />
                    <span className="font-medium">{tonightEvents[currentEventIndex].time}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-3xl font-bold text-le-accent">
                    {tonightEvents[currentEventIndex].price}
                  </div>
                  <div className="flex space-x-3">
                    <button className="btn-primary text-lg px-6 py-3">
                      Get Tickets
                    </button>
                    <button className="btn-secondary text-lg px-6 py-3">
                      Details
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Upcoming Events Preview */}
            <div className="space-y-4">
              {tonightEvents
                .filter((_, index) => index !== currentEventIndex)
                .slice(0, 2)
                .map((event, index) => (
                  <motion.div
                    key={event.id}
                    className="glass-card p-6 hover:bg-white/10 transition-all duration-300 cursor-pointer group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                        <img
                          src={event.image}
                          alt={event.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-white mb-1 group-hover:text-le-accent transition-colors duration-300">
                          {event.title}
                        </h5>
                        <p className="text-white/70 mb-2">{event.artist}</p>
                        <div className="flex items-center space-x-4 text-sm text-white/60">
                          <span>{event.venue}</span>
                          <span>{event.time}</span>
                          <span className="text-le-accent font-medium">{event.price}</span>
                        </div>
                      </div>
                      <ArrowRight size={20} className="text-white/40 group-hover:text-le-accent transition-colors duration-300" />
                    </div>
                  </motion.div>
                ))}
            </div>

            <motion.div
              className="text-center pt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              <button className="text-le-accent hover:text-le-accent-2 transition-colors duration-300 flex items-center space-x-2 mx-auto text-lg font-medium">
                <span>View Full Calendar</span>
                <ArrowRight size={18} />
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom CTA Section */}
        <motion.div
          className="mt-24 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
        >
          <div className="glass-card p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-le-accent/10 via-transparent to-le-accent-2/10"></div>
            <div className="relative z-10">
              <h2 className="text-4xl font-display font-bold text-white mb-6">
                Join Wichita's Music Revolution
              </h2>
              <p className="text-xl text-white/70 mb-8 max-w-3xl mx-auto">
                Whether you're performing, discovering, or just passionate about music - your community awaits.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <button className="btn-primary text-xl px-12 py-6 flex items-center space-x-3">
                  <Music size={24} />
                  <span>Create Artist Profile</span>
                </button>
                <button className="btn-accent text-xl px-12 py-6 flex items-center space-x-3">
                  <Headphones size={24} />
                  <span>Explore Community</span>
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default HeroSection